import { zodResolver } from '@hookform/resolvers/zod';
import { Box, Typography } from '@mui/material';
import Grid from '@mui/material/Grid';
import { useEffect, useMemo, useRef } from 'react';
import type { DefaultValues } from 'react-hook-form';
import { useForm, useFormContext, useWatch } from 'react-hook-form';
import { useRevalidator } from 'react-router';
import useSWR, { type Fetcher, useSWRConfig } from 'swr';

import {
  createFormInvalidHandler,
  FieldAndCell,
  formatLiftAPIClientError,
} from '~/components/form/utils';
import { buildFieldDef, RENDERED_TICKET_FIELD_NAMES } from '~/components/tickets/addTicketFields';
import {
  invoicesForProperty,
  isInvoiceSelectionStale,
  withOutOfScopeInvoice,
} from '~/components/tickets/invoiceLinking';
import type {
  LAIUseFormReturn,
  LiftAIFormProps,
} from '~/components/uploadDocument/SelectDocumentTypeDialog';
import { associateFileToEntity } from '~/components/utils/upload';
import { invoicesList } from '~/data/generated/endpoints/invoices/invoices';
import { propertiesFormList } from '~/data/generated/endpoints/properties/properties';
import { ticketsCreate, ticketsPartialUpdate } from '~/data/generated/endpoints/tickets/tickets';
import type { PropertyForm } from '~/data/generated/model';
import { useTicketProblemTypes } from '~/data/hooks/useTicketProblemTypes';
import { parseApiResponse } from '~/data/parseApiResponse';
import type { InvoiceFormOption } from '~/data/schemas/invoice';
import { invoiceFormOptionSchema } from '~/data/schemas/invoice';
import type {
  API_TicketCreateRequest,
  EntryTypeKey,
  Ticket,
  TicketDto,
  TicketDtoValues,
} from '~/data/schemas/ticket';
import { MaintenanceType, ticketDtoSchema, ticketIsCallback } from '~/data/schemas/ticket';
import useLAISnackbar, { ActionEnum } from '~/hooks/useLAISnackbar';
import { useLocaleTerms } from '~/hooks/useLocaleTerms';
import { usePropertyCars } from '~/hooks/usePropertyCars';
import { useProviders } from '~/hooks/useProviders';
import { toDateTimeLocalInTimezone } from '~/utils/timezone';

/**
 * A ticket-form validation error is on a field the user cannot see or fix when
 * its top-level key is not in {@link RENDERED_TICKET_FIELD_NAMES}. That is the
 * AMP-585 silent-save class (`startedTimeHolidayName`): the schema required a
 * field the form never renders, so the submit was blocked with nothing on
 * screen. Compared on the top-level segment because RHF keys errors by field.
 */
export const isUnrenderedTicketField = (fieldPath: string): boolean =>
  !RENDERED_TICKET_FIELD_NAMES.has(fieldPath.split('.')[0]);

const ticketOptionsFetcher: Fetcher<
  {
    properties: PropertyForm[];
    invoices: InvoiceFormOption[];
  },
  readonly [key: string]
> = async ([_]) => {
  const [propertiesResponse, invoicesResponse] = await Promise.all([
    propertiesFormList(),
    // variant=form returns the trimmed form field set (a bare array); the
    // generated union type is pinned by the InvoiceDisplay[] consumer, so cast
    // at this boundary (AMP-503).
    invoicesList({ variant: 'form' }),
  ]);
  // variant=form is unpaginated → a bare array today, but normalize the envelope
  // shape too so a future PAGE_SIZE can't silently break the dropdown.
  const invoicesRaw = invoicesResponse.data;
  const invoicesResults = Array.isArray(invoicesRaw) ? invoicesRaw : invoicesRaw.results;
  const invoices = parseApiResponse(
    invoiceFormOptionSchema.array(),
    invoicesResults,
    'InvoiceFormOption',
  );

  return { properties: propertiesResponse.data, invoices };
};

export const getDefaultValues = (): DefaultValues<TicketDtoValues> => {
  return {
    number: '',
    entryType: MaintenanceType.Visit as EntryTypeKey,
    propertyId: undefined,
    carIds: [],
    invoiceId: null,
    workPerformed: '',
    callTime: '',
    startedTime: '',
    finishedTime: '',
    problemDescription: '',
    notes: '',
    timeOnSiteRegularHours: '',
    travelTimeRegularHours: '',
    timeOnSiteOvertime: '',
    travelTimeOvertime: '',
    problemType: undefined,
    serviceProviderGroup: undefined,
    maintenanceHours: undefined,
  };
};

const defaultDisabledFields = {
  number: false,
  entryType: false,
  propertyId: false,
  carIds: false,
  invoiceId: false,
  workPerformed: false,
  callTime: false,
  startedTime: false,
  finishedTime: false,
  problemDescription: false,
  notes: false,
  timeOnSiteRegularHours: false,
  travelTimeRegularHours: false,
  timeOnSiteOvertime: false,
  travelTimeOvertime: false,
  equipmentFailure: null,
  problemType: false,
  maintenanceHours: false,
} as const;

export const mapTicketToFormValues = (ticket: Ticket): TicketDto => {
  const propertyTimeZone = ticket.property?.timeZone;

  const base = {
    ...getDefaultValues(),
    id: ticket.id,
    propertyId: ticket.property?.id ?? undefined,
    invoiceId: ticket.invoice ? ticket.invoice.id : null,
    serviceProviderGroup: ticket.serviceProviderGroup?.id ?? undefined,
    carIds: ticket.carsServiced?.map((car) => car.id) ?? [],
    entryType: ticket.entryType,
    number: ticket.number,
    startedTime: toDateTimeLocalInTimezone(ticket.startedTime, propertyTimeZone),
    finishedTime: toDateTimeLocalInTimezone(ticket.finishedTime, propertyTimeZone),
    workPerformed: ticket.workPerformed,
    timeOnSiteRegularHours: ticket.timeOnSiteRegularHours,
    travelTimeRegularHours: ticket.travelTimeRegularHours,
    timeOnSiteOvertime: ticket.timeOnSiteOvertime,
    travelTimeOvertime: ticket.travelTimeOvertime,
    equipmentFailure: ticket.equipmentFailure,
    notes: ticket.notes,
    maintenanceHours: ticket.maintenanceHours ?? undefined,
  } as const;

  if (ticketIsCallback(ticket)) {
    return {
      ...base,
      callTime: ticket.callTime
        ? toDateTimeLocalInTimezone(ticket.callTime, propertyTimeZone)
        : null,
      problemDescription: ticket.problemDescription,
      problemType: ticket.problemType,
    } as TicketDto;
  }

  return {
    ...base,
  } as TicketDto;
};

const createOrUpdateTicket = async (dto: TicketDto): Promise<Ticket> => {
  const { propertyId, invoiceId, carIds, serviceProviderGroup, ...payload } = dto;

  const ticketPayload: API_TicketCreateRequest = {
    ...payload,
    property: propertyId,
    invoice: invoiceId,
    carsServiced: carIds,
    serviceProviderGroup,
  };

  // Route create/update through the generated SDK (update is a PATCH, so it maps
  // to ticketsPartialUpdate). The form still works in terms of the hand-written
  // API_TicketCreateRequest / Ticket shapes (camelCase payload unchanged), so
  // cast at this boundary (AMP-502).
  const { data: ticket } = ticketPayload.id
    ? await ticketsPartialUpdate(
        ticketPayload.id,
        ticketPayload as unknown as Parameters<typeof ticketsPartialUpdate>[1],
      )
    : await ticketsCreate(ticketPayload as unknown as Parameters<typeof ticketsCreate>[0]);

  // The create/update response is the (sparser) write serializer, not the full
  // read Ticket, so it can't be parsed against ticketSchema; cast here.
  return ticket as unknown as Ticket;
};

export const useTicketForm = (): LAIUseFormReturn<TicketDtoValues, TicketDto> => {
  const hookForm = useForm<TicketDtoValues, unknown, TicketDto>({
    // Use mode: 'all' for quick debugging
    mode: 'onSubmit',
    defaultValues: getDefaultValues(),
    resolver: zodResolver(ticketDtoSchema),
  });

  return { hookForm, Form: TicketForm, Fields: TicketFields };
};

const TicketForm = ({
  children,
  document,
  onSubmit,
  initialData,
}: LiftAIFormProps<TicketDtoValues, Ticket>) => {
  const { mutate } = useSWRConfig();
  const { showEntityActionSnackbar, showSnackbar } = useLAISnackbar();
  const { handleSubmit, clearErrors, reset, resetField, control } =
    useFormContext<TicketDtoValues>();
  const revalidator = useRevalidator();

  const ticketType = useWatch({ control, name: 'entryType' });
  const propertyId = useWatch({ control, name: 'propertyId' });

  // Clear errors when ticket type changes, otherwise we get stuck with old errors
  useEffect(() => {
    clearErrors();
  }, [ticketType, clearErrors]);

  useEffect(() => {
    if (!initialData) {
      return;
    }

    const formValues = {
      ...getDefaultValues(),
      ...initialData,
    };

    reset(formValues);
  }, [initialData, reset]);

  useEffect(() => {
    if (propertyId) {
      resetField('carIds');
    }
  }, [propertyId, resetField]);

  return (
    <form
      noValidate
      style={{ display: 'flex', flexDirection: 'column', height: '100%' }}
      onSubmit={handleSubmit(
        async (data, e) => {
          try {
            const ticketDto = ticketDtoSchema.parse(data);
            const ticket = await createOrUpdateTicket(ticketDto);

            if (document) {
              await associateFileToEntity(
                document.attachment.id,
                ticket.id,
                'ticket',
                document.fileType === 'pdf'
                  ? {
                      pageNumber: document.pageNum,
                      isPrimary: true,
                    }
                  : {},
              );
            }

            if (!data.serviceProviderGroup) {
              throw new Error('Service Provider Group is required');
            }
            onSubmit(ticket);

            const action = data.id ? ActionEnum.Update : ActionEnum.Create;
            showEntityActionSnackbar(
              {
                name: 'Ticket',
                action,
                id: data.number,
              },
              {
                variant: 'info',
              },
            );

            // We need to revalidate everything because the ticket changes can effect KPI calculations
            // and there are too many fetchers to keep track of that may be affected
            // TODO: We should be more specific about what we revalidate
            void revalidator.revalidate();
            void mutate((key) => true);
          } catch (error) {
            console.error(error);
            const errorMessage = formatLiftAPIClientError('Failed to save ticket', error);

            showSnackbar(errorMessage, {
              variant: 'error',
            });
          }
        },
        // A blocked submit must never be invisible again (the AMP-585 incident:
        // the resolver rejected on `startedTimeHolidayName`, a field the form
        // never renders, so RHF blocked Save with no request and no on-screen
        // error). The shared handler always toasts and reports to Sentry.
        createFormInvalidHandler<TicketDtoValues>({
          formName: 'TicketForm',
          showSnackbar,
          isHiddenField: isUnrenderedTicketField,
        }),
      )}
    >
      {children}
    </form>
  );
};

export const TicketFields: React.FC = () => {
  const { control, setValue } = useFormContext<TicketDtoValues>();
  const terms = useLocaleTerms();
  const propertyId = useWatch({ control, name: 'propertyId' });
  const ticketType = useWatch({ control, name: 'entryType' });
  const selectedInvoiceId = useWatch({ control, name: 'invoiceId' });
  const { data } = useSWR(['TicketForm.Options'], ticketOptionsFetcher);

  const { cars } = usePropertyCars(propertyId);
  // Disable invoiceId field when no property is selected
  const disabledFieldsWithInvoice = useMemo(() => {
    return {
      ...defaultDisabledFields,
      invoiceId: !propertyId,
    };
  }, [propertyId]);

  const invoices = useMemo(
    () => invoicesForProperty(data?.invoices, propertyId),
    [data, propertyId],
  );

  // A property change re-filters the dropdown but leaves the chosen invoice in
  // form state, and that stale id is what gets submitted. Only on a real
  // change — a value arriving already set (a synchronous default, or a later
  // reset() applying loaded data) must not be judged, or a legacy mismatch
  // gets silently unlinked on the next save. (AMP-467)
  //
  // The property-change tracking and the staleness check run on separate
  // clocks. `propertyForInvoice` always advances so a real move is never
  // missed even if several happen before the list loads; `staleCheckPending`
  // remembers that a move (not a load) is owed a check, and the check itself
  // waits for `data` — collapsing several pending moves into one check against
  // the final state once the list is trustworthy, rather than letting an
  // in-between one silently skip validation. (AMP-467)
  const propertyForInvoice = useRef(propertyId);
  const staleCheckPending = useRef(false);
  useEffect(() => {
    if (propertyForInvoice.current !== propertyId) {
      staleCheckPending.current = !!propertyForInvoice.current;
      propertyForInvoice.current = propertyId;
    }
    if (!data || !staleCheckPending.current) return;
    staleCheckPending.current = false;
    if (isInvoiceSelectionStale(selectedInvoiceId, invoices)) {
      setValue('invoiceId', null, { shouldDirty: true });
    }
  }, [data, invoices, propertyId, selectedInvoiceId, setValue]);

  // A resolved invoice from another property (AMP-610's root cause) is
  // excluded from `invoices`, so without this it renders blank instead of a
  // visible, clearable value once it's already sitting in `invoiceId`.
  // (AMP-693)
  const invoiceOptions = useMemo(
    () => withOutOfScopeInvoice(invoices, data?.invoices, selectedInvoiceId),
    [invoices, data?.invoices, selectedInvoiceId],
  );

  const serviceProviders = useProviders({ propertyId });
  const { problemTypes } = useTicketProblemTypes();
  // Find the selectedInvoiceId in the invoices array
  const selectedInvoice = invoices.find((invoice) => invoice.id === selectedInvoiceId);

  // If the invoiceId is set, filter the serviceProviders to only include the ones that are associated with the invoice else return all serviceProviders
  const filteredServiceProviders = useMemo(() => {
    return selectedInvoice?.serviceProvider?.id
      ? serviceProviders.providers?.filter(
          (provider) => selectedInvoice?.serviceProvider?.id === provider.id,
        )
      : serviceProviders.providers;
  }, [selectedInvoice, serviceProviders]);

  const fields = useMemo(
    () =>
      buildFieldDef({
        ticketType,
        properties: data?.properties,
        cars,
        invoices: invoiceOptions,
        serviceProviderGroup: filteredServiceProviders ?? [],
        disabledFields: disabledFieldsWithInvoice,
        problemTypes,
        terms,
      }),
    [
      data?.properties,
      ticketType,
      cars,
      invoiceOptions,
      disabledFieldsWithInvoice,
      filteredServiceProviders,
      problemTypes,
      terms,
    ],
  );

  const { generalFields, ticketFields, notesField } = fields;

  return (
    <>
      <Typography variant="h6" my={2}>
        General
      </Typography>
      <Grid container spacing={2} my={2}>
        {generalFields.map((field) => (
          <FieldAndCell key={field.name} field={field} control={control} />
        ))}
      </Grid>
      <Box my={2}>
        <Typography variant="h6" py={2}>
          Ticket
        </Typography>
        <Grid container spacing={2}>
          {ticketFields.map((field) => (
            <FieldAndCell key={field.name} field={field} control={control} />
          ))}
        </Grid>
      </Box>
      <Grid container spacing={1} mt={2}>
        <FieldAndCell field={notesField} control={control} />
      </Grid>
    </>
  );
};
